import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Zap, Building2, Globe, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const standardPlans = [
  {
    name: "Site Vitrine",
    price: "990",
    priceType: "unique",
    monthly: "29",
    description: "Site professionnel cle en main avec referencement local",
    features: [
      "Site responsive 5-7 pages",
      "Design personnalise",
      "Referencement SEO local",
      "Formulaire de contact",
      "Certificat SSL inclus",
      "Hebergement 1 an inclus",
      "Formation utilisation",
    ],
    cta: "Demander un devis",
    popular: false,
  },
  {
    name: "Pack SEO + Reseaux",
    price: "149",
    priceType: "monthly",
    description: "Visibilite maximale sur Google et les reseaux sociaux",
    features: [
      "Audit SEO complet",
      "Optimisation Google Maps",
      "2 posts/jour Facebook + Instagram",
      "Rapport mensuel de performance",
      "Gestion de votre fiche Google",
      "Suivi des mots-cles",
      "Veille concurrentielle",
    ],
    cta: "Essai gratuit 7 jours",
    popular: true,
  },
  {
    name: "E-commerce",
    price: "2490",
    priceType: "unique",
    monthly: "49",
    description: "Boutique en ligne complete avec paiement et logistique",
    features: [
      "Boutique Stripe integree",
      "Gestion de stock",
      "Paiement securise",
      "SEO produits",
      "Emails automatises",
      "Dashboard ventes",
      "Support prioritaire",
    ],
    cta: "Demander un devis",
    popular: false,
  },
];

const agencyPlans = [
  {
    name: "Starter",
    price: "49",
    priceType: "monthly",
    description: "Pour les agences qui demarrent leur digitalisation",
    features: [
      "Jusqu'a 5 clients",
      "Gestion des leads + CRM",
      "Propositions commerciales",
      "Gestion de projets",
      "Facturation automatique",
      "Module SEO",
      "Support par email",
    ],
    cta: "Essai gratuit 7 jours",
    popular: false,
  },
  {
    name: "Pro",
    price: "149",
    priceType: "monthly",
    description: "Pour les agences en croissance avec des besoins avances",
    features: [
      "Jusqu'a 25 clients",
      "Tout le plan Starter",
      "Reseaux sociaux (2 posts/jour)",
      "Veille concurrentielle",
      "Dashboard KPI avance",
      "Emails automatises",
      "Support prioritaire",
    ],
    cta: "Choisir Pro",
    popular: true,
  },
  {
    name: "Agency",
    price: "349",
    priceType: "monthly",
    description: "Pour les grandes agences et groupes multi-marques",
    features: [
      "Clients illimites",
      "Tout le plan Pro",
      "White-label complet",
      "Automatisations avancees",
      "API & Webhooks",
      "Multi-utilisateurs",
      "Support dedie + onboarding",
    ],
    cta: "Contacter l'equipe",
    popular: false,
  },
];

export default function PricingPage() {
  const [tab, setTab] = useState("standard");

  const plans = tab === "standard" ? standardPlans : agencyPlans;

  return (
    <div className="min-h-screen" data-testid="pricing-page">
      <section className="pt-24 pb-10 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
            Des offres adaptees<br />
            <span className="text-[#0099CC]">a chaque besoin</span>
          </h1>
          <p className="text-[#94A3B8] mt-5 text-lg max-w-2xl mx-auto">
            Que vous soyez un professionnel cherchant a booster votre presence en ligne
            ou une agence gerant plusieurs clients, nous avons la solution.
          </p>

          {/* Tab switcher */}
          <div className="flex justify-center mt-8 gap-1 p-1 bg-[#0B0F19] rounded-xl border border-[#2A3441] inline-flex">
            <button onClick={() => setTab("standard")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                tab === "standard" ? "bg-[#0099CC] text-white shadow-lg" : "text-[#94A3B8] hover:text-white"
              }`} data-testid="tab-standard">
              <Globe className="h-4 w-4" />Client Standard
            </button>
            <button onClick={() => setTab("agency")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                tab === "agency" ? "bg-[#F59E0B] text-black shadow-lg" : "text-[#94A3B8] hover:text-white"
              }`} data-testid="tab-agency">
              <Building2 className="h-4 w-4" />Client Agence
            </button>
          </div>

          <p className="mt-4 text-xs text-[#64748b]">
            {tab === "standard"
              ? "Vous etes un professionnel et cherchez a developper votre activite en ligne ou evenementielle."
              : "Vous etes une agence web/marketing et souhaitez gerer vos clients avec votre propre plateforme CRM."}
          </p>
        </div>
      </section>

      <section className="pb-24 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div key={plan.name}
              className={`relative rounded-2xl border p-8 flex flex-col transition-all ${
                plan.popular
                  ? "border-[#0099CC] bg-[#0099CC]/5 shadow-lg shadow-[#0099CC]/10 scale-[1.02]"
                  : "border-[#2A3441] bg-[#151B2B] hover:border-[#3A4451]"
              }`}
              data-testid={`plan-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}>
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#0099CC] text-white text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
                  <Zap className="h-3 w-3" />Populaire
                </div>
              )}

              <h3 className="text-xl font-bold text-white">{plan.name}</h3>
              <p className="text-[#94A3B8] text-sm mt-2 min-h-[40px]">{plan.description}</p>

              <div className="mt-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-[#64748b]">EUR{plan.priceType === "monthly" ? "/mois" : ""}</span>
                </div>
                {plan.monthly && (
                  <p className="text-xs text-[#64748b] mt-1">+ {plan.monthly} EUR/mois maintenance</p>
                )}
              </div>

              <ul className="mt-8 space-y-3 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <Check className="h-4 w-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <span className="text-[#CBD5E1]">{f}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contact" className="mt-8 block">
                <Button className={`w-full py-3 font-semibold ${
                  plan.popular
                    ? "bg-[#0099CC] hover:bg-[#0088B5] text-white"
                    : "bg-[#1E293B] hover:bg-[#2A3441] text-white border border-[#2A3441]"
                }`} data-testid={`cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  {plan.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto mt-12 text-center">
          <p className="text-[#94A3B8] text-sm">
            Tous les plans avec abonnement incluent un essai gratuit de 7 jours. Aucune carte bancaire requise.
            <br />Annulez a tout moment. Devis sur mesure disponible.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 border-t border-[#2A3441]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-10">Questions frequentes</h2>
          <div className="space-y-4">
            {[
              { q: "Quelle est la difference entre Client Standard et Client Agence ?", a: "Le Client Standard beneficie de nos prestations (creation de site, SEO, reseaux sociaux). Le Client Agence utilise GLOTECH OS comme sa propre plateforme CRM pour gerer ses clients en white-label." },
              { q: "Puis-je changer de plan a tout moment ?", a: "Oui, vous pouvez upgrader ou downgrader votre plan a tout moment depuis votre tableau de bord." },
              { q: "Y a-t-il un engagement ?", a: "Non, tous nos plans mensuels sont sans engagement. Les prestations sur-mesure font l'objet d'un devis." },
              { q: "Comment fonctionne l'essai gratuit ?", a: "Vous avez 7 jours pour tester toutes les fonctionnalites. Aucune carte bancaire n'est requise pour commencer." },
            ].map((faq, i) => (
              <div key={i} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 hover:border-[#3A4451] transition-colors">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <ChevronDown className="h-4 w-4 text-[#0099CC]" />{faq.q}
                </h4>
                <p className="text-[#94A3B8] text-sm mt-2 ml-6">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
